package org.moxultimate.topwithmox.wallpaper.panorama

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.url.URLSearchParams

/**
 * PanoramaScreen
 * Fullscreen interactive 360° equirectangular image viewer powered by native WebGL.
 * Features: drag-to-pan with inertia, scroll/pinch-to-zoom, HiDPI canvas,
 * loading spinner, drag hint, auto-rotate until first interaction, mobile touch.
 */
@Composable
fun PanoramaScreen() {
    val imageSrc = URLSearchParams(window.location.search)
        .get("src")
        ?.takeIf { it.isNotBlank() }
        ?: "/360_gojo.webp"

    DisposableEffect(imageSrc) {
        val script = document.createElement("script") as HTMLScriptElement
        script.id = "panorama-js"
        script.text = PANORAMA_JS
        document.head!!.appendChild(script)
        /* Pass the Kotlin runtime value through window.asDynamic() so the
           js() literal can reference it via a stable property name. */
        window.asDynamic().__panoramaSrc = imageSrc
        js("window.__panoramaInit('panorama-canvas', window.__panoramaSrc)")

        onDispose {
            js("if(window.__panoramaDestroy)window.__panoramaDestroy()")
            document.getElementById("panorama-js")
                ?.let { document.head?.removeChild(it) }
            js("delete window.__panoramaInit; delete window.__panoramaDestroy; delete window.__panoramaSrc;")
        }
    }

    Canvas(
        attrs = Modifier
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("width", "100%")
                property("height", "100%")
                property("display", "block")
                property("background", "#0a0a0a")
                property("touch-action", "none")
            }
            .toAttrs { id("panorama-canvas") }
    )
}

/**
 * PANORAMA_JS
 * Self-contained WebGL 360° equirectangular sphere viewer.
 *
 * Improvements over v1:
 * - 128 × 256 sphere segments (vs 64 × 128) — smoother projection
 * - Inertia/momentum: velocity decays with friction after drag release
 * - Mouse-wheel + pinch-to-zoom: FOV range 22.5° – 110° (starts at 90°)
 * - Drag sensitivity scales with current FOV (natural zoom-in feel)
 * - HiDPI/Retina: canvas buffer matches devicePixelRatio (capped 2×)
 * - Mipmapping: LINEAR_MIPMAP_LINEAR for crisp rendering at any zoom
 * - Loading spinner overlay that fades on image load
 * - "Drag to explore · Scroll to zoom" hint fades after 3 s
 * - lon=90 initial angle so panorama center faces the camera
 * - Auto-rotate at 0.025°/frame stops on first user interaction
 * - Full cleanup via window.__panoramaDestroy()
 */
@Suppress("JSUnusedLocalSymbols")
private val PANORAMA_JS = """
(function(){
  var raf, onResize, onMMove, onMUp, onWheel, cvRef, fsBtn, onFsChange;

  function init(id, src) {
    var cv = document.getElementById(id);
    if (!cv) return;
    cvRef = cv;

    /* ── Loading overlay ─────────────────────────────────────────────────── */
    var ov = document.createElement('div');
    ov.id = 'pano-ov';
    ov.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;background:#0a0a0a;' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'z-index:99;transition:opacity 0.8s ease;pointer-events:none;';
    ov.innerHTML =
      '<div style="width:52px;height:52px;border:3px solid rgba(255,255,255,0.12);' +
        'border-top-color:#fff;border-radius:50%;animation:pSpin 0.85s linear infinite;"></div>' +
      '<p style="margin:18px 0 0;color:rgba(255,255,255,0.55);font-family:sans-serif;' +
        'font-size:12px;letter-spacing:2.5px;text-transform:uppercase;">Loading 360°</p>';
    document.body.appendChild(ov);

    if (!document.getElementById('pano-css')) {
      var sty = document.createElement('style');
      sty.id  = 'pano-css';
      sty.textContent = '@keyframes pSpin{to{transform:rotate(360deg)}}';
      document.head.appendChild(sty);
    }

    /* ── WebGL context ───────────────────────────────────────────────────── */
    var gl = cv.getContext('webgl', {antialias:true, alpha:false}) ||
             cv.getContext('experimental-webgl');
    if (!gl) {
      ov.innerHTML =
        '<p style="color:#fff;font-size:15px;font-family:sans-serif;' +
        'padding:40px;text-align:center;">WebGL is not supported in this browser.</p>';
      return;
    }

    /* ── Shaders ─────────────────────────────────────────────────────────── */
    var VS =
      'attribute vec3 aPos;attribute vec2 aUV;uniform mat4 uMVP;varying vec2 vUV;' +
      'void main(){gl_Position=uMVP*vec4(aPos,1.0);vUV=aUV;}';
    var FS =
      'precision highp float;uniform sampler2D uTex;varying vec2 vUV;' +
      'void main(){gl_FragColor=texture2D(uTex,vUV);}';

    function mkSh(type, code) {
      var s = gl.createShader(type);
      gl.shaderSource(s, code); gl.compileShader(s); return s;
    }
    var prog = gl.createProgram();
    gl.attachShader(prog, mkSh(gl.VERTEX_SHADER,  VS));
    gl.attachShader(prog, mkSh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog); gl.useProgram(prog);

    var locPos = gl.getAttribLocation(prog,  'aPos');
    var locUV  = gl.getAttribLocation(prog,  'aUV');
    var locMVP = gl.getUniformLocation(prog, 'uMVP');
    var locTex = gl.getUniformLocation(prog, 'uTex');

    /* ── Sphere geometry ─────────────────────────────────────────────────── */
    /* 128 lat x 256 lon bands. Max vertex index = 128*257+256 = 33152 < 65535 (Uint16 OK) */
    var LA = 128, LO = 256;
    var pos = [], uvs = [], idx = [];

    for (var la = 0; la <= LA; la++) {
      var theta = la * Math.PI / LA;
      var sT = Math.sin(theta), cT = Math.cos(theta);
      for (var lo = 0; lo <= LO; lo++) {
        var phi = lo * 6.283185307179586 / LO;
        pos.push(-Math.cos(phi) * sT, cT, Math.sin(phi) * sT);
        uvs.push(1.0 - lo / LO, la / LA);
      }
    }
    for (var i = 0; i < LA; i++) {
      for (var j = 0; j < LO; j++) {
        var a = i * (LO + 1) + j, b = a + LO + 1;
        idx.push(a, b, a + 1, b, b + 1, a + 1);
      }
    }

    function mkBuf(target, data) {
      var buf = gl.createBuffer();
      gl.bindBuffer(target, buf);
      gl.bufferData(target, data, gl.STATIC_DRAW);
      return buf;
    }
    var bufPos  = mkBuf(gl.ARRAY_BUFFER,        new Float32Array(pos));
    var bufUV   = mkBuf(gl.ARRAY_BUFFER,        new Float32Array(uvs));
    var bufIdx  = mkBuf(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx));
    var idxCount = idx.length;

    /* ── Texture ─────────────────────────────────────────────────────────── */
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA,
                  gl.UNSIGNED_BYTE, new Uint8Array([10, 10, 10, 255]));

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      /* WebGL 1 requires power-of-two dimensions for mipmap filters.
         Use LINEAR_MIPMAP_LINEAR only when both dimensions are POT; otherwise
         fall back to LINEAR to avoid a silently incomplete (black) texture. */
      function isPOT(v) { return v > 0 && (v & (v - 1)) === 0; }
      if (isPOT(img.naturalWidth) && isPOT(img.naturalHeight)) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.generateMipmap(gl.TEXTURE_2D);
      } else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      }
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      ov.style.opacity = '0';
      setTimeout(function(){ if(ov.parentNode) ov.parentNode.removeChild(ov); }, 900);
      showHint();
    };
    img.onerror = function() {
      ov.style.opacity = '1';
      ov.innerHTML =
        '<p style="color:#fff;font-size:15px;font-family:sans-serif;' +
        'padding:40px;text-align:center;">Could not load 360° image.</p>';
    };
    img.src = src;

    /* Drag-to-explore hint */
    function showHint() {
      var h = document.createElement('div');
      h.style.cssText =
        'position:fixed;bottom:28px;left:50%;transform:translateX(-50%);' +
        'color:rgba(255,255,255,0.8);font-family:sans-serif;font-size:12px;' +
        'letter-spacing:1px;background:rgba(0,0,0,0.5);padding:7px 18px;' +
        'border-radius:20px;pointer-events:none;transition:opacity 1.2s ease;z-index:10;';
      h.textContent = 'Drag to explore · Scroll to zoom';
      document.body.appendChild(h);
      setTimeout(function(){ h.style.opacity = '0'; }, 2600);
      setTimeout(function(){ if(h.parentNode) h.parentNode.removeChild(h); }, 3900);
    }

    /* Fullscreen toggle button (top-right). Hidden where the Fullscreen API is unsupported (e.g. iPhone Safari). */
    var root = document.documentElement;
    var canFs = !!(root.requestFullscreen || root.webkitRequestFullscreen);
    if (canFs) {
      var ICON_ENTER = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';
      var ICON_EXIT  = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h3a2 2 0 0 0 2-2V3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M21 16h-3a2 2 0 0 0-2 2v3"/></svg>';
      fsBtn = document.createElement('button');
      fsBtn.id = 'pano-fs';
      fsBtn.setAttribute('aria-label', 'Toggle full screen');
      fsBtn.title = 'Full screen';
      fsBtn.style.cssText =
        'position:fixed;top:20px;right:20px;width:42px;height:42px;display:flex;' +
        'align-items:center;justify-content:center;background:rgba(0,0,0,0.5);' +
        'border:1px solid rgba(255,255,255,0.25);border-radius:50%;cursor:pointer;' +
        'padding:0;z-index:100;transition:background 0.2s ease;';
      fsBtn.innerHTML = ICON_ENTER;
      fsBtn.addEventListener('mouseenter', function(){ fsBtn.style.background = 'rgba(0,0,0,0.75)'; });
      fsBtn.addEventListener('mouseleave', function(){ fsBtn.style.background = 'rgba(0,0,0,0.5)'; });
      fsBtn.addEventListener('mousedown', function(e){ e.stopPropagation(); });
      fsBtn.addEventListener('touchstart', function(e){ e.stopPropagation(); });
      fsBtn.addEventListener('click', function() {
        var active = document.fullscreenElement || document.webkitFullscreenElement;
        if (active) {
          (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        } else {
          (root.requestFullscreen || root.webkitRequestFullscreen).call(root);
        }
      });
      onFsChange = function() {
        var active = document.fullscreenElement || document.webkitFullscreenElement;
        fsBtn.innerHTML = active ? ICON_EXIT : ICON_ENTER;
        fsBtn.title = active ? 'Exit full screen' : 'Full screen';
      };
      document.addEventListener('fullscreenchange', onFsChange);
      document.addEventListener('webkitfullscreenchange', onFsChange);
      document.body.appendChild(fsBtn);
    }

    /* ── Matrix helpers (all column-major, matching WebGL uniformMatrix4fv) ── */
    /* col-major: element at (row r, col c) lives at flat index c*4+r             */
    function mul4(a, b) {
      var o = new Float32Array(16);
      for (var c = 0; c < 4; c++)
        for (var r = 0; r < 4; r++) {
          var s = 0;
          for (var k = 0; k < 4; k++) s += a[k*4+r] * b[c*4+k];
          o[c*4+r] = s;
        }
      return o;
    }
    function perspM(fov, asp, n, f) {
      var t = 1.0 / Math.tan(fov * 0.5), nf = 1.0 / (n - f), o = new Float32Array(16);
      /* col 0 */ o[0]=t/asp;
      /* col 1 */ o[5]=t;
      /* col 2 */ o[10]=(f+n)*nf; o[11]=-1.0;
      /* col 3 */ o[14]=2.0*f*n*nf;
      return o;
    }
    function rotXM(a) {
      /* col-major rotX: col1=[0,c,s,0], col2=[0,-s,c,0] */
      var c=Math.cos(a), s=Math.sin(a), o=new Float32Array(16);
      o[0]=1; o[5]=c; o[6]=s; o[9]=-s; o[10]=c; o[15]=1; return o;
    }
    function rotYM(a) {
      /* col-major rotY: col0=[c,0,-s,0], col2=[s,0,c,0] */
      var c=Math.cos(a), s=Math.sin(a), o=new Float32Array(16);
      o[0]=c; o[2]=-s; o[5]=1; o[8]=s; o[10]=c; o[15]=1; return o;
    }

    /* ── View state ──────────────────────────────────────────────────────── */
    var D2R     = 0.017453292519943295;   /* PI / 180 */
    var lon     = 90.0;                   /* start facing panorama center (u=0.5) */
    var lat     = 0.0;
    var lonVel  = 0.0, latVel = 0.0;     /* inertia velocities (deg/frame) */
    var fov     = 1.5708;                 /* 90 deg in radians */
    var FOV_MIN = 0.3927;                 /* 22.5 deg */
    var FOV_MAX = 1.9199;                 /* 110 deg */
    var drag    = false;
    var lx      = 0, ly = 0;
    var autoRot = true;
    var dpr     = Math.min(window.devicePixelRatio || 1, 2);

    cv.style.cursor = 'grab';

    /* Drag sensitivity decreases as you zoom in for natural feel */
    function sens() { return fov / 1.5708 * 0.14; }

    function onDown(x, y) {
      drag = true; autoRot = false;
      lonVel = 0; latVel = 0;
      lx = x; ly = y;
      cv.style.cursor = 'grabbing';
    }
    function onMove(x, y) {
      if (!drag) return;
      var sp = sens();
      lonVel = (x - lx) * sp;
      latVel = -(y - ly) * sp;
      lon += lonVel;
      lat  = Math.max(-85, Math.min(85, lat + latVel));
      lx = x; ly = y;
    }
    function onUp() { drag = false; cv.style.cursor = 'grab'; }

    /* Mouse */
    cv.addEventListener('mousedown', function(e) { onDown(e.clientX, e.clientY); });
    onMMove = function(e) { onMove(e.clientX, e.clientY); };
    onMUp   = function()  { onUp(); };
    window.addEventListener('mousemove', onMMove);
    window.addEventListener('mouseup',   onMUp);

    /* Wheel zoom */
    onWheel = function(e) {
      e.preventDefault();
      var d = e.deltaY !== undefined ? e.deltaY : -(e.wheelDelta || 0);
      fov = Math.max(FOV_MIN, Math.min(FOV_MAX, fov + d * 0.0008));
    };
    cv.addEventListener('wheel', onWheel, { passive: false });

    /* Touch */
    var pinch0 = 0;
    cv.addEventListener('touchstart', function(e) {
      e.preventDefault();
      autoRot = false;
      if (e.touches.length === 1) {
        onDown(e.touches[0].clientX, e.touches[0].clientY);
      } else if (e.touches.length === 2) {
        drag = false;
        pinch0 = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY);
      }
    }, { passive: false });

    cv.addEventListener('touchend', function(e) {
      e.preventDefault();
      if (e.touches.length < 2) onUp();
    }, { passive: false });

    cv.addEventListener('touchmove', function(e) {
      e.preventDefault();
      if (e.touches.length === 1) {
        onMove(e.touches[0].clientX, e.touches[0].clientY);
      } else if (e.touches.length === 2) {
        var d = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY);
        if (pinch0 > 0) fov = Math.max(FOV_MIN, Math.min(FOV_MAX, fov * (pinch0 / d)));
        pinch0 = d;
      }
    }, { passive: false });

    /* ── Resize ──────────────────────────────────────────────────────────── */
    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width  = Math.round(window.innerWidth  * dpr);
      cv.height = Math.round(window.innerHeight * dpr);
      gl.viewport(0, 0, cv.width, cv.height);
    }
    resize();
    onResize = function() { resize(); };
    window.addEventListener('resize', onResize);

    /* ── Render loop ─────────────────────────────────────────────────────── */
    var FRICTION = 0.92;
    var AUTO_SPD = 0.025;   /* deg/frame */

    function frame() {
      if (autoRot) {
        lon += AUTO_SPD;
      } else if (!drag) {
        lonVel *= FRICTION;
        latVel *= FRICTION;
        lon += lonVel;
        lat  = Math.max(-85, Math.min(85, lat + latVel));
      }

      gl.clearColor(0.04, 0.04, 0.04, 1.0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);

      var asp = cv.width / (cv.height || 1);
      var mvp = mul4(perspM(fov, asp, 0.1, 100.0),
                     mul4(rotXM(lat * D2R), rotYM(lon * D2R)));
      gl.uniformMatrix4fv(locMVP, false, mvp);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(locTex, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, bufPos);
      gl.enableVertexAttribArray(locPos);
      gl.vertexAttribPointer(locPos, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, bufUV);
      gl.enableVertexAttribArray(locUV);
      gl.vertexAttribPointer(locUV, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufIdx);
      gl.drawElements(gl.TRIANGLES, idxCount, gl.UNSIGNED_SHORT, 0);

      raf = requestAnimationFrame(frame);
    }
    frame();
  }

  window.__panoramaInit = init;
  window.__panoramaDestroy = function() {
    if (raf)     cancelAnimationFrame(raf);
    if (onResize) window.removeEventListener('resize',    onResize);
    if (onMMove)  window.removeEventListener('mousemove', onMMove);
    if (onMUp)    window.removeEventListener('mouseup',   onMUp);
    if (onWheel && cvRef) cvRef.removeEventListener('wheel', onWheel);
    if (onFsChange) {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    }
    if (fsBtn && fsBtn.parentNode) fsBtn.parentNode.removeChild(fsBtn);
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen();
    var ov  = document.getElementById('pano-ov');
    var css = document.getElementById('pano-css');
    if (ov  && ov.parentNode)  ov.parentNode.removeChild(ov);
    if (css && css.parentNode) css.parentNode.removeChild(css);
  };
})();
""".trimIndent()
